import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BlogArticle, BlogCategory } from '../../../../mock-data/types';
import { renderArticleBody, safeUrl } from '../../markdownToHtml';
import { formatArticleDate } from '../../../blog/articleDate';
import {
  Root,
  Cover,
  CoverImg,
  CoverVignette,
  CoverInner,
  Breadcrumbs,
  Crumb,
  CrumbSep,
  CrumbCurrent,
  HubPill,
  PillDot,
  CoverTitle,
  Standfirst,
  Byline,
  BylineAuthorLink,
  AuthorCard,
  AuthorAvatar,
  AuthorName,
  AuthorRole,
  AuthorBio,
  AuthorLinks,
  CoverCredit,
  BodyArc,
  Layout,
  Main,
  AtAGlance,
  BoxLabel,
  AtAGlanceBody,
  PostBody,
  KeyTakeaways,
  TakeawayList,
  TakeawayItem,
  Sources,
  SourceList,
  LightHeading,
  Aside,
  AsideInner,
  Toc,
  TocLabel,
  TocList,
  TocLink,
  OutroPanel,
  Eyebrow,
  FaqItem,
  CardsGrid,
  PostCard,
  CardLabel,
  CardTitle,
  CardDescription,
  PageNav,
  PageNavLink,
  PageNavDir,
  PageNavTitle,
} from './ArticleView.styles';

/** wl reading-order key: pillar first, then sequence, then title. */
const readingOrder = (a: BlogArticle, b: BlogArticle): number =>
  Number(b.pillar) - Number(a.pillar) || a.sequence - b.sequence || a.title.localeCompare(b.title);

/** First occurrence wins, so the composed order (pillar → pinned → scored) holds. */
const dedupeById = (list: BlogArticle[]): BlogArticle[] => {
  const seen = new Set<string>();
  return list.filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true)));
};

/** wl related scoring: shared tags ×1 + shared entities ×2 (linking.py). */
const relatedScore = (article: BlogArticle, candidate: BlogArticle): number => {
  const sharedTags = candidate.tags.filter((tag) => article.tags.includes(tag)).length;
  const sharedEntities = candidate.entities.filter((entity) => article.entities.includes(entity)).length;
  return sharedTags + 2 * sharedEntities;
};

export type ArticleViewProps = {
  article: BlogArticle;
  /** The brand's whole blog: the source of prev/next, the pillar up-link and the scored related set. */
  articles: BlogArticle[];
  categories: BlogCategory[];
  /**
   * Opens another article of the cluster. Omit it where following a link would
   * cost the reader their place (the editor's preview sheet, over an unsaved
   * draft): the cards then render as inert, without the hover lift that promises
   * a destination.
   */
  onOpenArticle?: (id: string) => void;
  /**
   * Turns a `slug:` body cross-link into an href. Omit it alongside
   * {@link onOpenArticle}; unresolved links fall back to `#`.
   */
  resolveHref?: (slug: string) => string | undefined;
};

/**
 * The whitelabels.com article page replicated inside the studio, rendered from a
 * single article. This markup + `markdownToHtml.ts` + the BLOG_PREVIEW tokens are
 * the rendering contract the casino front-end implements 1:1 (plus the head/JSON-LD
 * checklist in `docs/blog-authoring-plan.md`).
 *
 * It takes the article as a prop rather than reading the store, because it serves
 * two callers with different sources of truth: the preview route renders the SAVED
 * article, and the editor's preview sheet renders the draft being typed, which has
 * never been near the store.
 */
export const ArticleView = ({
  article,
  articles,
  categories,
  onOpenArticle,
  resolveHref,
}: ArticleViewProps): React.ReactElement | null => {
  const { t, i18n } = useTranslation();

  const category = categories.find((c) => c.id === article.categoryId);

  const rendered = React.useMemo(
    () =>
      renderArticleBody(article.bodyMarkdown, {
        resolveSlug: (slug) => resolveHref?.(slug),
        calloutLabels: {
          tip: t('Tip'),
          warning: t('Heads up'),
          note: t('Note'),
          key: t('Key point'),
        },
      }),
    [article.bodyMarkdown, resolveHref, t],
  );

  const readMinutes = Math.max(1, Math.ceil(rendered.wordCount / 220));

  // Topic-cluster neighbourhood (linking.py port): reading order inside the
  // category for prev/next; pillar up-link as the first "Keep reading" card.
  const siblings = articles.filter((a) => a.categoryId === article.categoryId && a.id !== article.id);
  const orderedCluster = [article, ...siblings].sort(readingOrder);
  const position = orderedCluster.findIndex((a) => a.id === article.id);
  const prev = position > 0 ? orderedCluster[position - 1] : undefined;
  const next = position < orderedCluster.length - 1 ? orderedCluster[position + 1] : undefined;

  const pillar = !article.pillar ? siblings.find((a) => a.pillar) : undefined;
  const pinned = article.relatedSlugs
    .map((slug) => articles.find((a) => a.slug === slug))
    .filter((a): a is BlogArticle => Boolean(a));
  const scored = articles
    .filter(
      (a) =>
        a.id !== article.id &&
        a.id !== pillar?.id &&
        !pinned.some((p) => p.id === a.id) &&
        relatedScore(article, a) > 0,
    )
    .sort((a, b) => relatedScore(article, b) - relatedScore(article, a));
  // De-duplicated: the pillar is prepended unconditionally, but `pinned` comes
  // from the author's related-article chips, whose source list INCLUDES the
  // pillar — pinning it rendered the same card twice under one React key and
  // pushed a genuinely different suggestion out of the top 3.
  const keepReading = dedupeById([...(pillar ? [pillar] : []), ...pinned, ...scored]).slice(0, 3);

  const categoryLabel = (a: BlogArticle): string => categories.find((c) => c.id === a.categoryId)?.name ?? '';

  const inert = !onOpenArticle;
  const open = (id: string) => (): void => onOpenArticle?.(id);

  return (
    <Root>
      <Cover>
        {article.hero ? <CoverImg src={article.hero.url} alt={article.hero.alt} /> : null}
        <CoverVignette />
        <CoverInner>
          <Breadcrumbs aria-label={t('Breadcrumb')}>
            <Crumb>{t('Blog')}</Crumb>
            {category ? (
              <>
                <CrumbSep>/</CrumbSep>
                <Crumb>{category.name}</Crumb>
              </>
            ) : null}
            <CrumbSep>/</CrumbSep>
            <CrumbCurrent aria-current="page">{article.title}</CrumbCurrent>
          </Breadcrumbs>
          {category ? (
            <HubPill>
              <PillDot />
              {article.pillar ? t('Start here') : category.name}
            </HubPill>
          ) : null}
          <CoverTitle>{article.title}</CoverTitle>
          {article.description ? <Standfirst>{article.description}</Standfirst> : null}
          <Byline>
            {article.author?.name ? (
              <span>
                {t('Written by')}{' '}
                <BylineAuthorLink href="#article-author">{article.author.name}</BylineAuthorLink>
              </span>
            ) : null}
            {article.publishedAt ? (
              <span>{t('Published {{date}}', { date: formatArticleDate(article.publishedAt, i18n.language, 'long') })}</span>
            ) : null}
            <span>{t('Updated {{date}}', { date: formatArticleDate(article.updatedAt, i18n.language, 'long') })}</span>
            <span>{t('{{count}} min read', { count: readMinutes })}</span>
          </Byline>
        </CoverInner>
        {article.hero?.caption ? <CoverCredit>{article.hero.caption}</CoverCredit> : null}
      </Cover>

      <BodyArc>
        <Layout>
          <Main>
            {article.atAGlance ? (
              <AtAGlance className="at-a-glance">
                <BoxLabel text="At a glance" tag="span" />
                <AtAGlanceBody>{article.atAGlance}</AtAGlanceBody>
              </AtAGlance>
            ) : null}

            {/* Contract markup from markdownToHtml (escaped at render). */}
            <PostBody className="post-body" dangerouslySetInnerHTML={{ __html: rendered.html }} />

            {article.keyTakeaways.length > 0 ? (
              <KeyTakeaways className="key-takeaways">
                <LightHeading text="Key takeaways" tag="h2" />
                <TakeawayList>
                  {article.keyTakeaways.map((takeaway) => (
                    <TakeawayItem key={takeaway}>{takeaway}</TakeawayItem>
                  ))}
                </TakeawayList>
              </KeyTakeaways>
            ) : null}

            {article.sources.length > 0 ? (
              <Sources>
                <LightHeading text="Sources and further reading" tag="h2" />
                <SourceList>
                  {article.sources.map((source) => (
                    <li key={source.url}>
                      {/* Author-typed free text: React does NOT filter a `javascript:` href
                          on a JSX attribute, so the same allowlist the body renderer uses
                          runs here too. */}
                      <a href={safeUrl(source.url)} target="_blank" rel="noopener nofollow noreferrer">
                        {source.title || source.url}
                      </a>
                    </li>
                  ))}
                </SourceList>
              </Sources>
            ) : null}
          </Main>

          <Aside>
            <AsideInner>
              {rendered.headings.length >= 2 ? (
                <Toc aria-label={t('On this page')}>
                  <TocLabel text="On this page" tag="span" />
                  <TocList>
                    {rendered.headings.map((heading) => (
                      <li key={heading.id}>
                        <TocLink href={`#${heading.id}`}>{heading.text}</TocLink>
                      </li>
                    ))}
                  </TocList>
                </Toc>
              ) : null}
            </AsideInner>
          </Aside>
        </Layout>

        {article.author?.name ? (
          <OutroPanel id="article-author">
            <Eyebrow text="About the author" tag="span" />
            <AuthorCard>
              {article.author.avatarUrl ? (
                <AuthorAvatar src={article.author.avatarUrl} alt={article.author.name} loading="lazy" />
              ) : null}
              <div>
                <AuthorName>{article.author.name}</AuthorName>
                {article.author.role ? <AuthorRole>{article.author.role}</AuthorRole> : null}
                {article.author.bio ? <AuthorBio>{article.author.bio}</AuthorBio> : null}
                {article.author.links.length > 0 ? (
                  <AuthorLinks>
                    {article.author.links.map((link, index) => (
                      <li key={index}>
                        {/* Author-typed free text, so the href goes through the
                            same scheme allowlist the article body's links do. */}
                        <a href={safeUrl(link.url)} target="_blank" rel="noopener me noreferrer">
                          {link.label || link.url}
                        </a>
                      </li>
                    ))}
                  </AuthorLinks>
                ) : null}
              </div>
            </AuthorCard>
          </OutroPanel>
        ) : null}

        {article.faq.length > 0 ? (
          <OutroPanel>
            <Eyebrow text="Questions" tag="span" />
            <LightHeading text="Frequently asked questions" tag="h2" />
            <div>
              {article.faq.map((item) => (
                <FaqItem key={item.q}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </FaqItem>
              ))}
            </div>
          </OutroPanel>
        ) : null}

        {keepReading.length > 0 || prev || next ? (
          <OutroPanel>
            {keepReading.length > 0 ? (
              <>
                <Eyebrow text="More guides" tag="span" />
                <LightHeading text="Keep reading" tag="h2" />
                <CardsGrid>
                  {keepReading.map((related) => (
                    <PostCard
                      key={related.id}
                      as={inert ? 'div' : undefined}
                      type={inert ? undefined : 'button'}
                      $inert={inert}
                      onClick={inert ? undefined : open(related.id)}
                    >
                      <CardLabel>{related.id === pillar?.id ? t('Start here') : categoryLabel(related)}</CardLabel>
                      <CardTitle>{related.title}</CardTitle>
                      <CardDescription>{related.description}</CardDescription>
                    </PostCard>
                  ))}
                </CardsGrid>
              </>
            ) : null}
            {prev || next ? (
              <PageNav aria-label={t('More in this series')}>
                {prev ? (
                  <PageNavLink
                    as={inert ? 'div' : undefined}
                    type={inert ? undefined : 'button'}
                    $inert={inert}
                    onClick={inert ? undefined : open(prev.id)}
                  >
                    <PageNavDir>{t('Previous')}</PageNavDir>
                    <PageNavTitle>{prev.title}</PageNavTitle>
                  </PageNavLink>
                ) : null}
                {next ? (
                  <PageNavLink
                    as={inert ? 'div' : undefined}
                    type={inert ? undefined : 'button'}
                    $next
                    $inert={inert}
                    onClick={inert ? undefined : open(next.id)}
                  >
                    <PageNavDir>{t('Next')}</PageNavDir>
                    <PageNavTitle>{next.title}</PageNavTitle>
                  </PageNavLink>
                ) : null}
              </PageNav>
            ) : null}
          </OutroPanel>
        ) : null}
      </BodyArc>
    </Root>
  );
};

export default ArticleView;
